import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import {
  fetchCurrentSeason,
  fetchSeason,
  fetchSeasonStandings,
  fetchSeasonsList,
  STANDINGS_PAGE_SIZE,
  type PastSeasonView,
  type SeasonStanding,
} from '../api/seasons';
import { LetterTiles } from '../components/ui/LetterTiles';
import { Seo } from '../components/Seo';
import { TierLadder } from '../components/season/TierLadder';
import { StandingsList } from '../components/season/StandingsList';
import { monthLabel } from '../components/season/SeasonStrip';

const fmt = (n: number) => n.toLocaleString('en-GB');

function Countdown({ days }: { days: number }) {
  return (
    <p
      data-testid="season-countdown"
      className="mx-auto mt-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-stone-900 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-stone-300"
    >
      <span data-testid="season-days-remaining" className="tabular-nums text-amber-300">
        {days}
      </span>
      {days === 1 ? 'day left' : 'days left'}
    </p>
  );
}

/** The panel a closed season shows above its standings — the frozen top-N
 *  Platinum winners for that month, or an explanation when none were
 *  awarded (a quiet month under the minimum-participants guard).
 *
 *  Renders `season.platinum` directly — it already carries
 *  `leaderboard_name`, derived server-side from the same ranked query
 *  `standings` uses. Deliberately NOT `standings.slice(0, N)`: that used
 *  to be how this panel worked, on the assumption that the two lists were
 *  guaranteed to agree — they weren't (two independent orderings that
 *  happened to match), so a positional slice could silently award the
 *  medal to the wrong name. */
function PlatinumPanel({ season }: { season: PastSeasonView }) {
  const winners = season.platinum;

  return (
    <div
      data-testid="platinum-panel"
      className="mt-5 overflow-hidden rounded-lg text-center shadow-[0_3px_0_rgba(217,199,154,0.9)]"
      style={{ backgroundColor: '#2f3b4a' }}
    >
      <div className="px-4 py-3.5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-200">
          Platinum
        </p>
        {winners.length > 0 ? (
          <ul className="mt-1.5 space-y-0.5">
            {winners.map((w) => (
              <li key={w.user_id} className="text-sm font-bold text-[#fffdf6]">
                {w.leaderboard_name}
              </li>
            ))}
          </ul>
        ) : (
          <p data-testid="platinum-none" className="mt-1.5 text-sm text-stone-300">
            Not awarded this season.
          </p>
        )}
      </div>
    </div>
  );
}

/** A labelled block of standings. Two of them appear on a busy season: the
 *  leaders, and the rows either side of the viewer. Without the labels the
 *  second block reads as a broken continuation of the first — the ranks jump
 *  from 25 to 2,486. */
function StandingsBlock({
  label,
  standings,
  meRank,
  testId,
}: {
  label: string;
  standings: SeasonStanding[];
  meRank?: number | null;
  testId?: string;
}) {
  if (standings.length === 0) return null;
  return (
    <div className="mt-5" data-testid={testId}>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-600">
        {label}
      </p>
      <StandingsList standings={standings} meRank={meRank ?? null} />
    </div>
  );
}

/**
 * `/season` (the open season) and `/season/:key` (a past one). A
 * composition of the tier ladder, the standings list and — for a closed
 * past season — the Platinum panel; the pieces that do real work live in
 * their own components under `components/season/`.
 */
export default function Season() {
  const { key } = useParams<{ key?: string }>();
  const isPast = key !== undefined;
  const { user, loading: authLoading } = useAuth();
  // How many EXTRA pages of the top the viewer has asked for. Page 1 always
  // arrives with the season payload, so this starts at zero and nobody pays
  // for a second request unless they ask.
  const [extraTopPages, setExtraTopPages] = useState(0);

  // Fetched even on a past-season page — not just to render the open
  // season's own content, but so the switcher below always knows the
  // current key and can dedupe it against the "Current" button rather
  // than listing it twice.
  const currentQuery = useQuery({
    // Keyed by viewer — see the matching comment in Landing.tsx. Sharing one
    // key across auth states let a guest's `me: null` payload persist after
    // sign-in, so this page rendered no "You · … rank …" summary and no
    // highlighted row until the cache went stale.
    queryKey: ['seasons', 'current', user?.id ?? 'guest'],
    queryFn: fetchCurrentSeason,
    enabled: !authLoading,
  });
  const pastQuery = useQuery({
    queryKey: ['seasons', key],
    queryFn: () => fetchSeason(key!),
    enabled: isPast,
  });
  const seasonsListQuery = useQuery({
    queryKey: ['seasons', 'list'],
    queryFn: fetchSeasonsList,
  });

  const loading = isPast ? pastQuery.isLoading : authLoading || currentQuery.isLoading;
  const isError = isPast ? pastQuery.isError : currentQuery.isError;

  const seasonKey = isPast ? key : currentQuery.data?.key;
  const wordmark = seasonKey ? monthLabel(seasonKey).toUpperCase() : '';

  const view = isPast ? pastQuery.data : currentQuery.data;
  const basePage = view?.standings ?? [];
  const totalPlayers = view?.total_players ?? 0;

  // Pages 2..N of the top, fetched only once asked for. Page 1 already came
  // with the season payload, so this stays empty for everyone who never
  // presses the button — which at 25 rows is nearly everyone.
  const extraPages = useQueries({
    queries: Array.from({ length: extraTopPages }, (_, i) => ({
      queryKey: ['seasons', seasonKey ?? '', 'standings', i + 2],
      queryFn: () => fetchSeasonStandings(seasonKey!, i + 2),
      enabled: Boolean(seasonKey),
    })),
  });

  const visibleTop = [...basePage, ...extraPages.flatMap((q) => q.data?.rows ?? [])];
  const canLoadMoreTop = visibleTop.length < totalPlayers && basePage.length >= STANDINGS_PAGE_SIZE;

  // Months other than the one being viewed — the only ones the switcher can
  // actually take you to. A brand-new deployment has none, and a switcher
  // whose sole button is the page you are already on reads as a broken
  // control rather than navigation, so the whole nav is withheld until there
  // is somewhere to go.
  const otherSeasons = (seasonsListQuery.data?.seasons ?? []).filter(
    (k) => k !== currentQuery.data?.key,
  );

  // The viewer's own row, pre-built with a name attached — StandingsList
  // renders this as a pinned row at the foot of the list whenever `meRank`
  // doesn't fall inside the returned standings page (e.g. rank #40 on a
  // 25-row page), so the viewer can always find — and click through
  // from — their own row, not just a summary number above it. Requires
  // `user` for the name: `PlayerSeason` (the `me` shape) carries points,
  // rank and tier but not a leaderboard_name, and the signed-in player's
  // own pseudonym is already sitting on `useAuth().user`. `me` only ever
  // arrives from an authenticated request that succeeded, so by the time
  // it's here `user` is already settled too — but the `user &&` guard
  // still keeps this defensively null rather than passing a nameless row.

  return (
    <section data-testid="page-season">
      <Seo
        title="Season — Scrabble Daily"
        description="This month's season standings, tier ladder and Platinum winners."
        path={isPast ? `/season/${key}` : '/season'}
      />

      <header className="text-center">
        <h1 className="leading-none">
          {seasonKey ? (
            <LetterTiles word={wordmark} size="lg" label={`${wordmark} season`} />
          ) : null}
        </h1>
        {!isPast && currentQuery.data ? (
          <Countdown days={currentQuery.data.days_remaining} />
        ) : null}
        {isPast && pastQuery.data ? (
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-600">
            {pastQuery.data.closed ? 'Closed season' : 'Season in progress'}
          </p>
        ) : null}
      </header>

      {loading ? (
        <p role="status" className="mt-6 text-center text-stone-500">
          Loading season…
        </p>
      ) : isError ? (
        <p role="alert" className="mt-6 text-center text-red-600">
          Couldn't load this season — try again shortly.
        </p>
      ) : !isPast && currentQuery.data ? (
        <>
          {currentQuery.data.me ? (
            <p
              data-testid="season-me-summary"
              className="mt-4 rounded-lg border border-tile-edge bg-[#fffdf6] px-4 py-3 text-center text-sm font-semibold text-stone-800 shadow-[0_3px_0_rgba(217,199,154,0.9)]"
            >
              You · {fmt(currentQuery.data.me.points)} pts · rank{' '}
              {fmt(currentQuery.data.me.rank ?? 0)} of {fmt(currentQuery.data.total_players)}
            </p>
          ) : null}

          <div className="mt-5">
            <TierLadder
              thresholds={currentQuery.data.thresholds}
              points={currentQuery.data.me?.points ?? 0}
            />
          </div>

          <StandingsBlock
            label={
              currentQuery.data.neighbours.length > 0 ? 'Top of the season' : 'Season standings'
            }
            standings={visibleTop}
            meRank={currentQuery.data.me?.rank ?? null}
            testId="standings-top"
          />

          {canLoadMoreTop ? (
            <button
              type="button"
              data-testid="standings-show-more"
              onClick={() => setExtraTopPages((n: number) => n + 1)}
              className="mt-3 w-full rounded-md border border-tile-edge bg-tile-face py-2 text-xs font-bold uppercase tracking-[0.14em] text-stone-700 hover:bg-tile-face/70"
            >
              Show more of the top
            </button>
          ) : null}

          {/* Only when the viewer ranks below the visible top — otherwise
              they are already on screen and this would list them twice. */}
          <StandingsBlock
            label="Your position"
            standings={currentQuery.data.neighbours}
            meRank={currentQuery.data.me?.rank ?? null}
            testId="standings-neighbours"
          />
        </>
      ) : isPast && pastQuery.data ? (
        <>
          {pastQuery.data.closed ? <PlatinumPanel season={pastQuery.data} /> : null}
          <StandingsBlock
            label={pastQuery.data.neighbours.length > 0 ? 'Top of the season' : 'Final standings'}
            standings={pastQuery.data.standings}
            meRank={pastQuery.data.me?.rank ?? null}
            testId="standings-top"
          />
          <StandingsBlock
            label="Where you finished"
            standings={pastQuery.data.neighbours}
            meRank={pastQuery.data.me?.rank ?? null}
            testId="standings-neighbours"
          />
        </>
      ) : null}

      {otherSeasons.length > 0 ? (
        <nav
          data-testid="season-switcher"
          aria-label="Past seasons"
          className="mt-6 flex flex-wrap justify-center gap-2"
        >
          <Link
            to="/season"
            data-testid="season-switch-current"
            className={`rounded-md border-b-4 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] ${
              !isPast
                ? 'border-emerald-950 bg-emerald-800 text-white'
                : 'border-tile-edge bg-tile-face text-stone-700'
            }`}
          >
            Current
          </Link>
          {otherSeasons.map((k) => (
            <Link
              key={k}
              to={`/season/${k}`}
              data-testid={`season-switch-${k}`}
              className={`rounded-md border-b-4 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] ${
                isPast && key === k
                  ? 'border-emerald-950 bg-emerald-800 text-white'
                  : 'border-tile-edge bg-tile-face text-stone-700'
              }`}
            >
              {monthLabel(k)}
            </Link>
          ))}
        </nav>
      ) : null}
    </section>
  );
}
